'use client';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/accordion';
import NumberFormatter from '@/utils/bn';
import {
  CourseCurriculum,
  CourseFormat,
  groupBy,
} from '@hidayahlab/academy-suite-corelib';
import {
  AssignmentsIcon,
  BookOpen02Icon,
  Mic01Icon,
  ProfileIcon,
  Video01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useLocale, useTranslations } from 'next-intl';
import { ValueOf } from 'next/dist/shared/lib/constants';
import ModuleBadge from '../module-badge/module-badge';
import CurriculumContent from './curriculum-content';

type ModuleProps = {
  courseId: number;
  courseFormat: ValueOf<typeof CourseFormat>;
  curriculum: CourseCurriculum;
  isEnrolled: boolean;
  moduleIndex?: number;
  moduleId?: number;
  showResource: boolean;
  showActivity: boolean;
};

export default function CurriculumModule({
  moduleIndex,
  curriculum,
  moduleId,
  isEnrolled,
  courseFormat,
  courseId,
  showResource,
  showActivity,
}: ModuleProps) {
  const t = useTranslations();
  const contentGroupByType = groupBy(curriculum.contents, 'type');
  const locale = useLocale();

  return (
    <Accordion defaultValue={moduleId?.toString()} type="single" collapsible>
      <AccordionItem
        className="p-2 overflow-hidden rounded-sm md:p-4 md:pr-2"
        value={curriculum.id.toString()}
      >
        <AccordionTrigger className="pr-6">
          <div className="flex flex-col items-start pr-2 mb-2 gap-y-4">
            <ModuleBadge moduleIndex={(moduleIndex ?? 0) + 1} />
            <h4 className="text-lg font-semibold text-left text-gray-800">
              {curriculum.title}
            </h4>
          </div>
        </AccordionTrigger>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-1 sm:gap-2 pt-2 pb-3 text-xs text-gray-500 lg:text-sm">
          {contentGroupByType?.LESSON?.length > 0 && (
            <div className="flex items-center gap-1 min-w-0">
              <HugeiconsIcon
                icon={Video01Icon}
                className="size-4 sm:size-5 flex-shrink-0"
              />
              <p className="truncate">
                {locale === 'bn'
                  ? NumberFormatter(contentGroupByType?.LESSON?.length)
                  : contentGroupByType?.LESSON?.length}{' '}
                {courseFormat === 'LIVE'
                  ? t('course-details.live-classes')
                  : t('course-details.videos')}
              </p>
            </div>
          )}

          {contentGroupByType?.RESOURCE?.length > 0 && (
            <div className="flex items-center gap-1 min-w-0">
              <HugeiconsIcon
                icon={BookOpen02Icon}
                className="size-4 sm:size-5 flex-shrink-0"
              />
              <p className="truncate">
                {locale === 'bn'
                  ? NumberFormatter(contentGroupByType?.RESOURCE?.length)
                  : contentGroupByType?.RESOURCE?.length}{' '}
                {t('course-details.readings')}
              </p>
            </div>
          )}

          {contentGroupByType?.VOICE_QUIZ?.length > 0 && (
            <div className="flex items-center gap-1 min-w-0">
              <HugeiconsIcon
                icon={Mic01Icon}
                className="size-4 sm:size-5 flex-shrink-0"
              />
              <p className="truncate">
                {locale === 'bn'
                  ? NumberFormatter(contentGroupByType?.VOICE_QUIZ?.length)
                  : contentGroupByType?.VOICE_QUIZ?.length}{' '}
                {t('course-details.voice-quizzes')}
              </p>
            </div>
          )}

          {contentGroupByType?.ASSIGNMENT?.length > 0 && (
            <div className="flex items-center gap-1 min-w-0">
              <HugeiconsIcon
                icon={AssignmentsIcon}
                className="size-4 sm:size-5 flex-shrink-0"
              />
              <p className="truncate">
                {locale === 'bn'
                  ? NumberFormatter(contentGroupByType?.ASSIGNMENT?.length)
                  : contentGroupByType?.ASSIGNMENT?.length}{' '}
                {t('course-details.assignments')}
              </p>
            </div>
          )}

          {contentGroupByType?.QUIZ?.length > 0 && (
            <div className="flex items-center gap-1 min-w-0">
              <HugeiconsIcon
                icon={ProfileIcon}
                className="size-4 sm:size-5 flex-shrink-0"
              />
              <p className="truncate">
                {locale === 'bn'
                  ? NumberFormatter(contentGroupByType?.QUIZ?.length)
                  : contentGroupByType?.QUIZ?.length}{' '}
                {t('course-details.quizzes')}
              </p>
            </div>
          )}
        </div>

        <AccordionContent className="grid gap-4 px-0 pt-2 pb-0 overflow-y-auto border-t border-gray-200">
          {curriculum?.contents?.map((item, index) => (
            <CurriculumContent
              key={`${curriculum.id}-${item.id}-${index}`}
              type={item.type}
              courseFormat={courseFormat}
              id={item.id}
              label={item.title}
              lessonType={item.type === 'LESSON' ? item.lessonType : undefined}
              liveClassDate={
                item.type === 'LESSON' &&
                (item.lessonType === 'LIVE' || item.lessonType === 'SUPPORT')
                  ? item.classDate
                  : undefined
              }
              courseId={courseId}
              isEnrolled={isEnrolled}
              isFree={
                item.type !== 'QUIZ' &&
                item.type !== 'VOICE_QUIZ' &&
                item.type !== 'ASSIGNMENT' &&
                item.isFree
              }
              contentLength={curriculum.contents.length}
              showResource={showResource}
              showActivity={showActivity}
            />
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
